import React, { useState, useEffect } from "react";
import { BookOpen, TrendingUp, TrendingDown, Wallet, Landmark, CheckCircle2, Download } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import { useAuth } from "../../context/AuthContext";
import { ledger as api, expenses as expensesApi, type LedgerEntry, type BalanceSheet, type ApiError } from "../../services/api";
import DataGrid from "../../components/DataGrid";
import { downloadBlob } from "../../utils/downloadBlob";

const fmtAmount = (n: number | undefined) =>
  `₹${(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statCard = {
  background: "var(--nx-surface-1)",
  border: "1px solid var(--nx-border)",
  borderRadius: 12,
  padding: "16px 18px",
  flex: 1,
  minWidth: 160,
};

const LedgerPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [summary, setSummary] = useState<BalanceSheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [reconciledIds, setReconciledIds] = useState<Set<string>>(new Set());
  const [reconcileTarget, setReconcileTarget] = useState<LedgerEntry | null>(null);
  const [bankReference, setBankReference] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [reconcileError, setReconcileError] = useState("");

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    Promise.all([api.getLedger(societyId), api.getBalanceSheet(societyId)])
      .then(([l, b]) => { setEntries(l); setSummary(b); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [societyId]);

  const handleExportCsv = async () => {
    if (!societyId) return;
    try {
      const blob = await api.exportCsv(societyId);
      downloadBlob(blob, "ledger-export.csv");
    } catch { /* ignore */ }
  };

  const handleReconcile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !reconcileTarget) return;
    setReconciling(true); setReconcileError("");
    try {
      await expensesApi.reconcile(societyId, reconcileTarget.id, bankReference);
      setReconciledIds(p => new Set(p).add(reconcileTarget.id));
      setReconcileTarget(null); setBankReference("");
    } catch (err) {
      setReconcileError((err as ApiError).message || "Failed to reconcile.");
    } finally { setReconciling(false); }
  };

  return (
    <PageLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Ledger &amp; Balance Sheet
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>{entries.length} entries · income and expenses in chronological order</p>
        </div>
        <button onClick={handleExportCsv} className="nx-btn nx-btn-outline" style={{ gap: 6 }}>
          <Download size={14} /> Export to Tally (CSV)
        </button>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <div style={statCard}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--nx-text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
            <Wallet size={13} /> Cash on Hand
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "var(--nx-text-primary)" }}>{fmtAmount(summary?.cashOnHand)}</div>
        </div>
        <div style={statCard}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--nx-text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
            <Landmark size={13} /> Dues Receivable
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#D97706" }}>{fmtAmount(summary?.duesReceivable)}</div>
        </div>
        <div style={statCard}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--nx-text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
            <TrendingUp size={13} /> Total Income
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#10B981" }}>{fmtAmount(summary?.totalIncome)}</div>
        </div>
        <div style={statCard}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--nx-text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
            <TrendingDown size={13} /> Total Expense
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#DC2626" }}>{fmtAmount(summary?.totalExpense)}</div>
        </div>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            { key: "date", header: "Date", cellStyle: { color: "var(--nx-text-muted)" }, render: (e: LedgerEntry) => e.date },
            {
              key: "type", header: "Type",
              render: (e: LedgerEntry) => (
                <span style={{ padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 500, background: e.type === "INCOME" ? "rgba(16,185,129,0.1)" : "rgba(220,38,38,0.1)", color: e.type === "INCOME" ? "#10B981" : "#DC2626" }}>
                  {e.type === "INCOME" ? "Income" : "Expense"}
                </span>
              ),
            },
            { key: "particulars", header: "Particulars", cellStyle: { color: "var(--nx-text-secondary)" }, render: (e: LedgerEntry) => e.particulars },
            {
              key: "amount", header: "Amount",
              cellStyle: { fontWeight: 600 },
              render: (e: LedgerEntry) => (
                <span style={{ color: e.type === "INCOME" ? "#10B981" : "#DC2626" }}>
                  {e.type === "INCOME" ? "+" : "−"}{fmtAmount(e.amount)}
                </span>
              ),
            },
            { key: "balance", header: "Running Balance", cellStyle: { fontWeight: 600, color: "var(--nx-text-primary)" }, render: (e: LedgerEntry) => fmtAmount(e.runningBalance) },
            {
              key: "reconcile", header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (e: LedgerEntry) => {
                if (e.type !== "EXPENSE") return null;
                if (reconciledIds.has(e.id)) {
                  return <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#10B981", fontSize: 11 }}><CheckCircle2 size={12} /> Reconciled</span>;
                }
                return (
                  <button
                    onClick={() => { setReconcileTarget(e); setBankReference(""); setReconcileError(""); }}
                    className="nx-btn nx-btn-outline nx-btn-sm"
                    style={{ fontSize: 10 }}
                  >
                    Reconcile
                  </button>
                );
              },
            },
          ]}
          rows={entries}
          rowKey={e => e.id}
          loading={loading}
          emptyIcon={<BookOpen size={32} style={{ color: "var(--nx-text-muted)" }} />}
          emptyText="No ledger entries yet — pay an invoice or log an expense to get started."
        />
      </div>

      {reconcileTarget && (
        <Modal
          title={`Reconcile — ${reconcileTarget.particulars}`}
          submitLabel="Mark Reconciled"
          error={reconcileError}
          isLoading={reconciling}
          onClose={() => setReconcileTarget(null)}
          onSubmit={handleReconcile}
        >
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--nx-text-secondary)", marginBottom: 5, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Bank Reference *
            </label>
            <input
              value={bankReference}
              onChange={e => setBankReference(e.target.value)}
              required placeholder="Statement line ref / UTR number"
              style={{ width: "100%", padding: "9px 12px", background: "var(--nx-surface-3)", border: "1px solid var(--nx-border)", borderRadius: 8, fontSize: 13, color: "var(--nx-text-primary)", outline: "none", boxSizing: "border-box" }}
              autoFocus
            />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default LedgerPage;
